import { Link } from 'react-router-dom';

import { AnchorHeading } from '../components/AnchorHeading';
import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

export function SupportGuidelinesGuide() {
  useDocsPage('Write guidelines for the support AI — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Write guidelines for the AI</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Write <em>guidelines</em> for the support AI.
        </h1>
        <p className="dp-oneliner">
          Your help articles tell the AI what your product does. Guidelines tell it how to answer:
          the tone to take with each kind of customer, the context your articles leave out, when to
          ask a question first, and which questions to leave to your team. This guide explains what
          guidelines are, which type to use when, and how to add one.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="what">What a guideline is</AnchorHeading>
        <p className="dp-section-sub">
          A guideline is a short instruction your team writes for the AI, the way you would brief a
          new teammate on their first day. Every live guideline that applies to a customer is read
          with each answer the AI writes for them, beside the help articles that customer may read.
          The AI follows a guideline as an instruction. An article is only ever information it
          answers from.
        </p>
        <Figure
          title="What the AI reads for each answer"
          caption="Articles say what is true. Guidelines say how to answer. Only articles are shown to the customer as sources."
        >
          <Flow
            steps={[
              { title: 'The customer asks', text: 'In the chat, as a customer with their roles.' },
              {
                title: 'Their guidelines',
                text: 'Every live guideline for everyone, plus those for the customer’s roles.',
                tone: 'accent',
              },
              {
                title: 'Their help articles',
                text: 'The passages the customer may read that match the question.',
              },
              {
                title: 'The answer',
                text: 'In the tone you set, from both, with the articles as sources.',
              },
            ]}
          />
        </Figure>
        <p className="dp-section-sub">Three things to know from the start:</p>
        <ul className="dp-connects">
          <li>
            <strong>An answer can rest on a guideline.</strong> When no article covers a question
            but a guideline does (your support email address, where a feature lives), the AI can
            answer from the guideline.
          </li>
          <li>
            <strong>Customers never see your guidelines.</strong> Under an answer they see only the
            help articles it used. A guideline is never shown, linked or quoted as a source.
          </li>
          <li>
            <strong>The AI still never makes things up.</strong> Every statement must come from an
            article or a guideline. A guideline can change how the AI answers, never that rule.
          </li>
        </ul>
      </section>

      <section className="dp-section">
        <AnchorHeading id="types">The three types, and when to use each</AnchorHeading>
        <p className="dp-section-sub">
          Every guideline has a type. The type tells your team what the guideline is for and tells
          the AI how to apply it. Pick the one that matches what you want the AI to do differently.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Communication style</div>
            <div className="dp-field-v">
              <strong>How to say it.</strong> The tone, the words, and how much to explain, often
              for one role. Use it when the right answer is already in your articles but different
              customers need it told differently: a first-time applicant needs reassurance and plain
              steps; an administrator wants the exact setting, without hand-holding.
              <br />
              <em>Example:</em> &ldquo;When answering applicants, use a warm and reassuring tone.
              Assume this may be their first time using the product, avoid technical terms, and
              explain the steps simply and in order.&rdquo;
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Context and clarification</div>
            <div className="dp-field-v">
              <strong>What to know, and when to ask.</strong> Use it for what your articles do not
              cover well: <strong>facts</strong> they leave out (your support address, the name a
              feature goes by, how customers actually use the product);{' '}
              <strong>clarifying questions</strong> for a request that could mean several things,
              which the AI asks before answering; and <strong>redirections</strong> for a question
              that is not yours to answer, such as one about a decision another organisation makes,
              where the AI says who to ask instead.
              <br />
              <em>Example:</em> &ldquo;When a customer asks where to find their &lsquo;milestone
              metrics&rsquo;, ask whether they mean the answers to a request form, the Milestones
              tab or the Analytics tab before answering. Skip the question if they already named one
              of the three.&rdquo;
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Handover</div>
            <div className="dp-field-v">
              <strong>What to leave alone.</strong> Questions the AI must leave to your team, even
              when an article seems to answer them. Use it for anything that needs a person&rsquo;s
              judgement or authority: refunds, cancellations, contract changes, complaints, legal or
              security reports. The AI says nothing and the ticket goes to your team, exactly as
              when no article answers (see{' '}
              <Link to="/docs/guides/support-ai#handover">Handover</Link>).
              <br />
              <em>Example:</em> &ldquo;Leave any question about a refund, a cancellation or a change
              to a contract to the team.&rdquo;
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          <strong>Guideline or help article?</strong> If customers would benefit from reading it
          themselves (how a feature works, the steps to do something), write a help article and add
          it to your <Link to="/docs/guides/support-knowledge">knowledge base</Link>. If it is about
          how to answer (tone, what to ask first, what not to answer) or a short fact that does not
          deserve its own page, write a guideline. A guideline holds at most 2,500 characters, so it
          is never the place for a whole procedure.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="add">Add a guideline</AnchorHeading>
        <UiPath
          steps={['Support', 'Guidelines', 'New guideline']}
          note="Admins and owners. Other agents can read every guideline but cannot change them."
        />
        <ol className="dp-connects">
          <li>
            Open <strong>Guidelines</strong> in the Support section of the sidebar and click{' '}
            <strong>New guideline</strong>, or the button in the section for the type you want.
          </li>
          <li>Choose the type.</li>
          <li>Give it a title your team will recognise.</li>
          <li>Write the guideline: when it applies and what the AI should do.</li>
          <li>
            Choose who it applies to, or leave <strong>Applies to</strong> empty for every customer.
          </li>
          <li>
            Leave <strong>Live</strong> ticked to use it from the next answer, or untick it to save
            it paused.
          </li>
        </ol>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Type</div>
            <div className="dp-field-v">
              Communication style, Context and clarification, or Handover. See above.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Title</div>
            <div className="dp-field-v">
              Up to 120 characters. For your team, such as &ldquo;Applicant tone&rdquo; or
              &ldquo;Refunds go to a person&rdquo;. The AI reads it too, so make it say what the
              guideline is about.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Guideline</div>
            <div className="dp-field-v">
              The instruction itself, up to 2,500 characters. A counter under the box shows how many
              you have used.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Applies to</div>
            <div className="dp-field-v">
              The roles it is for. Empty means everyone. With roles, only customers who hold at
              least one of them get it. Roles are the ones you define for your knowledge base (see{' '}
              <Link to="/docs/guides/support-setup#roles">Roles</Link>).
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Live</div>
            <div className="dp-field-v">
              A live guideline is read with every answer it applies to. A paused one is kept but not
              used, which is handy while you draft or for a rule that is only true at some times of
              year.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          <strong>Or ask the Assistant.</strong> Describe what you want in your own words, for
          example &ldquo;make the AI ask which tab they mean before answering questions about
          milestone metrics&rdquo;, and the <Link to="/docs/assistant">Revvly Assistant</Link>{' '}
          drafts the guideline, picks its type and roles, and shows it on a card you approve. It can
          also list your guidelines and edit or pause one. The same tools are available to Claude
          through the <Link to="/docs/api/mcp">MCP connection</Link>, where a change waits for an
          admin to approve it in Revvly.
        </p>
        <p className="dp-section-sub">
          Changes apply to the next answer: nothing needs to be republished. Click a guideline to
          edit it, or use <strong>Delete</strong> on its row to remove it. To keep a guideline
          without using it, pause it instead.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="write">Write a guideline that works</AnchorHeading>
        <ul className="dp-connects">
          <li>
            <strong>One topic per guideline.</strong> &ldquo;Applicant tone&rdquo; and
            &ldquo;Refunds go to a person&rdquo; are two guidelines, not one. They are easier to
            find, pause and change.
          </li>
          <li>
            <strong>Say when it applies, then what to do.</strong> &ldquo;When a customer asks for
            our support email, give support@example.com. Only give it when they ask for it
            directly.&rdquo;
          </li>
          <li>
            <strong>Use your customers&rsquo; words.</strong> Name the phrasings they actually use
            (&ldquo;growth update&rdquo;, &ldquo;milestone metrics&rdquo;) so the AI recognises the
            question.
          </li>
          <li>
            <strong>Give the exact question to ask.</strong> For a clarifying question, write it out
            and say when to skip it, so the AI does not ask something the customer has already
            answered.
          </li>
          <li>
            <strong>Name screens and menus as your product shows them</strong>, and keep facts
            current: the AI repeats what a guideline says as true.
          </li>
          <li>
            <strong>Use roles instead of &ldquo;if the customer is…&rdquo;.</strong> Let{' '}
            <strong>Applies to</strong> decide who gets a guideline. The AI is told the
            customer&rsquo;s roles, but a guideline that only reaches the right people is the surer
            way.
          </li>
          <li>
            <strong>Don&rsquo;t paste whole articles.</strong> If customers should read it, it
            belongs in the knowledge base, where it is shown as a source.
          </li>
        </ul>
      </section>

      <section className="dp-section">
        <AnchorHeading id="audience">Who gets a guideline</AnchorHeading>
        <p className="dp-section-sub">
          A customer gets every live guideline for everyone, plus every live guideline for any role
          they hold. Their roles are worked out when the AI answers, from what your system sends
          about them and your roles&rsquo; current rules, the same way as for help articles. A
          customer who matches no role gets only the guidelines for everyone.
        </p>
        <p className="dp-section-sub">
          Deleting a role takes it off every guideline. A guideline that was only for that role is
          paused rather than opened to everyone, so a tone meant for administrators never starts
          reaching applicants. The guideline shows why it was paused. Choose who it is for and save
          it to use it again.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="how">How the AI uses them</AnchorHeading>
        <ul className="dp-connects">
          <li>
            Guidelines are read in type order: communication style first, then context and
            clarification, then handover.
          </li>
          <li>
            A guideline outranks the AI&rsquo;s general rules on tone, length, and when to ask or
            redirect. It never outranks the rule that every statement needs a source.
          </li>
          <li>
            When no article matches a question, the AI still answers if a context and clarification
            guideline applies to that customer. If only communication style guidelines apply, it
            hands over without trying, since tone alone answers nothing.
          </li>
          <li>
            Only your team can write a guideline. Nothing in an article or in a customer&rsquo;s
            message can act as one, however it is worded.
          </li>
          <li>
            Guidelines apply to the AI&rsquo;s replies to customers. They do not change how your
            agents reply.
          </li>
        </ul>
      </section>

      <section className="dp-section">
        <AnchorHeading id="limits">Limits</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Title</div>
            <div className="dp-field-v">120 characters.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">One guideline</div>
            <div className="dp-field-v">2,500 characters.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Guidelines per workspace</div>
            <div className="dp-field-v">50, live and paused together.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">All live guidelines</div>
            <div className="dp-field-v">
              30,000 characters together. The page shows how much you use. Live guidelines can be
              read with every answer, so this keeps answers quick, affordable and focused. To add
              one past the limit, shorten another or pause one you no longer need.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="test">Check a guideline before you rely on it</AnchorHeading>
        <p className="dp-section-sub">
          <strong>In the sandbox.</strong> Write to support as a practice customer with the role the
          guideline is for, and ask the question it is about. The sandbox uses your real guidelines
          and knowledge base, and nothing reaches a real customer. See{' '}
          <Link to="/docs/guides/support-ai#sandbox">Try it in the sandbox</Link>.
        </p>
        <p className="dp-section-sub">
          <strong>Against your past conversations.</strong> If you are moving from Intercom,{' '}
          <Link to="/docs/guides/support-ai#compare">Compare with Intercom</Link> drafts an answer
          to each sampled conversation with the guidelines that customer would get today. Open a
          conversation to see <strong>Guidelines Revvly followed</strong>, each marked when the
          answer cited it. The grading model reads the same guidelines, so an answer that rests on
          one is judged with its source. The CSV export has a column for them. Run a new comparison
          after changing your guidelines to see the effect.
        </p>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/support-ai">How the AI first line works ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-knowledge">
              Build the knowledge base the AI answers from ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/support-setup">Set up Support for your team ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
